import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  DraftFetchOutcome,
  IDLE_DRAFT_STATUS,
  QuestionnaireDraftWriter,
  confirmQuestionnaireDraftSaveStage,
  failQuestionnaireDraftSave,
  fetchQuestionnaireDraft,
  fetchSavedQuestionnaireTasks,
  openQuestionnaireDraftWriter,
} from "../questionnaire_draft/coordinator";
import {
  QuestionnaireDraftEditorHandle,
  QuestionnaireDraftRestoreAssessment,
  QuestionnaireDraftSaveStage,
  QuestionnaireDraftSnapshot,
  QuestionnaireDraftTarget,
  SavedQuestionnaireSummary,
  assessQuestionnaireDraftRestore,
  getQuestionnaireDraftKey,
} from "../../model/questionnaire_draft";

// Where the recovery check for an open editor stands. `savedAt` is the server's time of the copy, or
// null for a copy this tab still holds and the server may not have yet.
export type QuestionnaireDraftRecovery =
  | { state: "checking" }
  // nothing to restore, or the researcher chose: edits are saved from here on
  | { state: "none" }
  // `questionsChanged`: the copy's questions or display logic differ from the saved task's
  | { state: "offer"; savedAt: string | null; questionsChanged: boolean }
  | {
      state: "conflict";
      savedAt: string | null;
      reason: "changed" | "missing";
      questionsChanged: boolean;
    }
  // the copy's create was sent and may have gone through; `savedList` is null when it failed to load
  | {
      state: "review_saved_work";
      savedAt: string | null;
      savedList: SavedQuestionnaireSummary[] | null;
    }
  | { state: "unrestorable"; reason: "unsupported" | "other_target" }
  | { state: "check_failed" };

const noSubscription = () => () => undefined;

/**
 * Connects a questionnaire task editor (`AddTaskPage`) to the recovery-draft coordinator, and holds the
 * recovery check that runs as the editor opens.
 *
 * Nothing the editor holds is saved until the check is over: a copy on the server, or one this tab
 * still holds from an editor closed earlier, is the only copy there is until the researcher restores
 * it, discards it, or the check finds none. A copy is offered only when it belongs to this editor and
 * the saved questionnaire it started from is unchanged; otherwise the researcher sees why and decides.
 * Closing the editor keeps the copy, and an edit not saved yet goes on being saved without it.
 *
 * `enabled` is whether the editor is open; one that is not a questionnaire task takes no part.
 *
 * Another tab signing in as the same identity ends this tab's scope and drops its writer; the refresh
 * then binds the tab to that sign-in. An editor already past its check goes on saving what it shows
 * under the new sign-in, since the copy the check found is the one it wrote itself. One still checking
 * checks again.
 */
export const useQuestionnaireDraft = ({ enabled }: { enabled: boolean }) => {
  const sessionNonce = useSelector(
    (state: { auth?: { sessionNonce?: string | null } }) => state?.auth?.sessionNonce ?? null,
  );
  const handleRef = useRef<QuestionnaireDraftEditorHandle | null>(null);
  // the editor's handle is replaced on every render and cleared as it unmounts; the last one is kept so
  // its final copy can still be read as it closes
  const lastHandleRef = useRef<QuestionnaireDraftEditorHandle | null>(null);
  const writerRef = useRef<QuestionnaireDraftWriter | null>(null);
  const [writer, setWriter] = useState<QuestionnaireDraftWriter | null>(null);
  const [recovery, setRecovery] = useState<QuestionnaireDraftRecovery | null>(null);
  const targetRef = useRef<QuestionnaireDraftTarget | null>(null);
  const offeredRef = useRef<QuestionnaireDraftSnapshot | null>(null);
  // a restored copy is applied in one commit; the gate opens once the editor reports it on screen
  const openGateOnChangeRef = useRef(false);
  // the researcher is editing: the check is over, and edits are saved
  const pastCheckRef = useRef(false);
  const editFreely = () => {
    pastCheckRef.current = true;
    setRecovery({ state: "none" });
  };

  const currentHandle = () => {
    if (handleRef.current) lastHandleRef.current = handleRef.current;
    return handleRef.current ?? lastHandleRef.current;
  };
  const readCopy = useCallback(() => currentHandle()?.readSnapshot() ?? null, []);

  const present = (assessment: QuestionnaireDraftRestoreAssessment, savedAt: string | null) => {
    switch (assessment.kind) {
      case "offer":
        offeredRef.current = assessment.snapshot;
        return setRecovery({
          state: "offer",
          savedAt,
          questionsChanged: assessment.questionsChanged,
        });
      case "conflict":
        offeredRef.current = assessment.snapshot;
        return setRecovery({
          state: "conflict",
          savedAt,
          reason: assessment.reason,
          questionsChanged: assessment.questionsChanged,
        });
      case "review_saved_work":
        offeredRef.current = assessment.snapshot;
        return setRecovery({
          state: "review_saved_work",
          savedAt,
          savedList: assessment.savedList,
        });
      case "unsupported":
      case "other_target":
        return setRecovery({ state: "unrestorable", reason: assessment.kind });
      case "needs_saved_tasks":
        return setRecovery({ state: "check_failed" });
    }
  };

  const check = useCallback(async (opened: QuestionnaireDraftWriter) => {
    const target = targetRef.current;
    const isCurrent = () => writerRef.current === opened;
    if (!target || !isCurrent()) return;
    pastCheckRef.current = false;
    setRecovery({ state: "checking" });

    const held = opened.heldCopy();
    const fetched: DraftFetchOutcome = held
      ? { kind: "found", draft: held, updatedAt: "" }
      : await fetchQuestionnaireDraft(getQuestionnaireDraftKey(target));
    if (!isCurrent()) return;
    // the session changed while the check was out: nothing of it is offered to whoever signed in next
    if (fetched.kind === "stale") return setRecovery(null);
    if (fetched.kind === "absent") {
      opened.openGate();
      return editFreely();
    }
    if (fetched.kind !== "found") return setRecovery({ state: "check_failed" });
    const savedAt = held ? null : fetched.updatedAt;

    let assessment = assessQuestionnaireDraftRestore({
      draft: fetched.draft,
      target,
      savedTaskList: null,
    });
    if (assessment.kind === "needs_saved_tasks") {
      const saved = await fetchSavedQuestionnaireTasks(target.methodId);
      if (!isCurrent()) return;
      if (saved.kind === "stale") return setRecovery(null);
      if (saved.kind !== "ok") {
        // a create that may have gone through stays unresolved: there is no starting over from here
        if (target.kind === "new") {
          offeredRef.current = assessment.snapshot;
          return setRecovery({ state: "review_saved_work", savedAt, savedList: null });
        }
        return setRecovery({ state: "check_failed" });
      }
      assessment = assessQuestionnaireDraftRestore({
        draft: fetched.draft,
        target,
        savedTaskList: saved.taskList,
      });
    }
    present(assessment, savedAt);
  }, []);

  useEffect(() => {
    if (!enabled) {
      pastCheckRef.current = false;
      return;
    }
    const first = currentHandle()?.readSnapshot();
    // not a questionnaire task
    if (!first) return;
    targetRef.current = first.target;
    const opened = openQuestionnaireDraftWriter({
      key: getQuestionnaireDraftKey(first.target),
      read: readCopy,
    });
    if (!opened) return;
    writerRef.current = opened;
    setWriter(opened);
    if (pastCheckRef.current) {
      opened.openGate({ restored: true });
      editFreely();
    } else {
      void check(opened);
    }

    return () => {
      writerRef.current = null;
      offeredRef.current = null;
      openGateOnChangeRef.current = false;
      opened.close();
      lastHandleRef.current = null;
      setWriter(null);
      setRecovery(null);
    };
    // the sign-in is a dependency so a tab bound to a later one opens a writer under it
  }, [enabled, sessionNonce, check, readCopy]);

  const onChange = useCallback(() => {
    currentHandle();
    const current = writerRef.current;
    if (!current) return;
    if (openGateOnChangeRef.current) {
      openGateOnChangeRef.current = false;
      current.openGate({ restored: true });
    }
    current.noteChange();
  }, []);

  // read by the status note itself, so a change of status re-renders the note and not the whole editor
  const subscribeStatus = useMemo(() => writer?.subscribe ?? noSubscription, [writer]);
  const getStatus = useCallback(() => writer?.getStatus() ?? IDLE_DRAFT_STATUS, [writer]);

  const actions = useMemo(
    () => ({
      // puts the copy on offer into the editor; it is saved again once it is on screen
      restore: () => {
        const snapshot = offeredRef.current;
        const handle = currentHandle();
        if (!snapshot || !handle || !writerRef.current) return;
        openGateOnChangeRef.current = true;
        handle.hydrate(snapshot);
        editFreely();
      },
      // Deletes the copy; the editor goes on from what it shows now, as a new create. The key is
      // renewed first, so the editor's content with it counts as the copy deleted, not a new edit.
      discard: () => {
        currentHandle()?.renewCreateKey();
        writerRef.current?.discard();
        offeredRef.current = null;
        editFreely();
      },
      // goes on without checking, after the check failed: the first edit replaces any copy there is
      startFresh: () => {
        currentHandle()?.renewCreateKey();
        writerRef.current?.openGate();
        editFreely();
      },
      retryCheck: () => {
        const current = writerRef.current;
        if (current) void check(current);
      },
      retrySaving: () => writerRef.current?.retry(),
    }),
    [check],
  );

  const binding = useMemo(
    () => ({
      handleRef,
      onChange,
      subscribeStatus,
      getStatus,
      onRetrySaving: actions.retrySaving,
      beginSave: (options: { createsTask: boolean; requiresSchedules: boolean }) =>
        writerRef.current?.beginSave(options) ?? null,
      markStaged: () => writerRef.current?.markStaged() ?? null,
      confirmSchedulesSaved: (ticketId: string) =>
        confirmQuestionnaireDraftSaveStage(ticketId, QuestionnaireDraftSaveStage.Schedule),
      failSave: failQuestionnaireDraftSave,
      discardCopy: () => writerRef.current?.discard(),
    }),
    [onChange, subscribeStatus, getStatus, actions],
  );

  return { binding, recovery, actions };
};
